/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/views/Dashboard/Dashboard.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  HStack,
  VStack,
  Icon,
  Center,
  Badge,
  Input,
} from "@chakra-ui/react";
import { 
  TrackerIcon, 
  PersonIcon, 
  DoublePlayIcon,
} from "components/Icons/Icons.js";
import { 
  FiRefreshCw, 
  FiInbox, 
  FiTrendingUp,
  FiBarChart2,
  FiUsers,
  FiActivity,
} from "react-icons/fi";
import { useHistory } from "react-router-dom";
import Chart from "react-apexcharts";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import SplashScreen from "components/SplashScreen/SplashScreen";

// dayjs plugin'lerini extend et
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// usePageVisibility kaldırıldı - Polling yok, sadece manuel yenileme

export default function Dashboard() {
  const history = useHistory();
  const { handleError } = useErrorHandler(); // Merkezi error handling - Toast gösterimi için
  const iconBlue = useColorModeValue("blue.500", "blue.500");
  const iconBoxInside = useColorModeValue("white", "white");
  const textColor = useColorModeValue("gray.700", "white");
  const headerTitleColor = useColorModeValue("white", "white");
  const headerMetaColor = useColorModeValue("whiteAlpha.900", "gray.300");
  const headerMetaIconColor = useColorModeValue("whiteAlpha.900", "blue.200");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textTableColor = useColorModeValue("gray.500", "white");
  const cardBg = useColorModeValue("white", "navy.800");

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalDevices: 0,
    totalFirms: 0,
  });
  const [deviceBrandStats, setDeviceBrandStats] = useState({
    labels: [],
    series: [],
  });
  const [nextMonthSubscriptions, setNextMonthSubscriptions] = useState([]);
  const [activeFirmCount, setActiveFirmCount] = useState(0);
  const [activeDeviceCount, setActiveDeviceCount] = useState(0);
  const [monthlyDeviceTrend, setMonthlyDeviceTrend] = useState({
    labels: [],
    series: [],
    thisMonth: 0,
    lastMonth: 0,
  });
  const [deviceSalesDates, setDeviceSalesDates] = useState([]); // Backend'den gelen satış tarihleri (ISO string)
  const [monthlyViewMode, setMonthlyViewMode] = useState("thisMonth"); // "thisMonth" | "lastMonth" | "range"
  const [trendStart, setTrendStart] = useState("");
  const [trendEnd, setTrendEnd] = useState("");
  const [stockModelSummary, setStockModelSummary] = useState([]);
  const [recentFirms, setRecentFirms] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Dashboard için özel optimizasyonlu endpoint kullan (sadece gerekli alanlar)
      const summaryRes = await api.get("/api/dashboard/summary").catch(() => ({ ok: false, json: () => null }));
      
      if (!summaryRes.ok) {
        throw new Error("Dashboard verileri yüklenemedi");
      }

      const summary = await summaryRes.json();
      if (!summary) {
        throw new Error("Dashboard verileri boş");
      }

      // Backend'de tüm hesaplamalar yapıldı, sadece state'e set ediyoruz
      
      // --- Genel sayılar ---
      setStats({
        totalDevices: summary.total_devices || 0,
        totalFirms: summary.total_firms || 0,
      });

      // --- Cihaz tipi / marka dağılımı (Backend'de hesaplanmış) ---
      setDeviceBrandStats({
        labels: summary.device_brand_stats?.labels || [],
        series: summary.device_brand_stats?.series || [],
      });

      // --- Satış tarihleri (günlük trend için) ---
      // Backend'den ISO format string listesi geliyor: ["2025-01-15", "2025-01-16", ...]
      setDeviceSalesDates(summary.device_sales_dates || []);

      // --- Önümüzdeki ay bitecek abonelikler (Backend'de filtrelenmiş ve sıralanmış) ---
      const nextMonthSubs = (summary.next_month_subscriptions || []).map((sub) => ({
        cihaz_id: sub.cihaz_id,
        plaka: sub.plaka,
        firma: sub.firma,
        dongu_tarihi: sub.dongu_tarihi, // ISO string olarak geliyor
      }));
      setNextMonthSubscriptions(nextMonthSubs);

      // --- Aktif firma ve aktif araç sayısı (Backend'de hesaplanmış) ---
      setActiveFirmCount(summary.active_firm_count || 0);
      setActiveDeviceCount(summary.active_device_count || 0);

      // --- Aylık cihaz satış trendi (Backend'de hesaplanmış) ---
      setMonthlyDeviceTrend({
        labels: summary.monthly_device_trend?.labels || [],
        series: summary.monthly_device_trend?.series || [],
        thisMonth: summary.monthly_device_trend?.thisMonth || 0,
        lastMonth: summary.monthly_device_trend?.lastMonth || 0,
      });

      // --- Stok durumu: model bazında sayımlar (Backend'de hesaplanmış) ---
      const stockSummary = (summary.stock_model_summary || []).map((item) => ({
        model: item.model,
        count: item.count,
      }));
      setStockModelSummary(stockSummary);

      // --- Son eklenen firmalar (Backend'de filtrelenmiş ve sıralanmış) ---
      const recent = (summary.recent_firms || []).map((f) => ({
        id: f.id,
        firma_ismi: f.firma_ismi,
        kayit_tarihi: f.kayit_tarihi, // ISO string olarak geliyor
      }));
      setRecentFirms(recent);

      setLastRefresh(new Date());
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster + Toast bildirimi
      const errorMessage = handleError(err, "Dashboard veri yükleme hatası");
      setError(errorMessage || "Veriler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.");
    } finally {
      setLoading(false);
    }
  }, [handleError]); // handleError dependency olarak eklendi

  useEffect(() => {
    // İlk yükleme - Polling kaldırıldı, sadece manuel yenileme
    fetchDashboardData();
  }, [fetchDashboardData]); // Sadece component mount olduğunda bir kez çalışır

  const chartTheme = useColorModeValue("light", "dark");

  // Cihaz tipi / marka dağılımı grafiği
  const brandChartData = useMemo(
    () => ({
      series: [
        {
          name: "Cihaz Sayısı",
          data: deviceBrandStats.series,
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 700,
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            borderRadius: 8,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: deviceBrandStats.labels,
          labels: {
            style: {
              colors: textTableColor,
            },
          },
        },
        yaxis: {
          title: {
            text: "Cihaz Sayısı",
          },
        },
        tooltip: {
          theme: chartTheme,
        },
      },
    }),
    [deviceBrandStats, textTableColor, chartTheme]
  );

  // Aktif firma / aktif araç grafiği
  const activityChartData = useMemo(
    () => ({
      series: [
        {
          name: "Adet",
          data: [activeFirmCount, activeDeviceCount],
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "45%",
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: ["Aktif Firma", "Aktif Araç"],
          labels: {
            style: {
              colors: textTableColor,
            },
          },
        },
        yaxis: {
          title: {
            text: "Adet",
          },
        },
        colors: ["#3182CE", "#38A169"],
        tooltip: {
          theme: chartTheme,
        },
      },
    }),
    [activeFirmCount, activeDeviceCount, textTableColor, chartTheme]
  );

  // Aylık araç ekleme / satış trend grafiği
  const monthlyTrendChartData = useMemo(() => {
    // Varsayılan: aylık toplamlar
    let labels = monthlyDeviceTrend.labels;
    let data = monthlyDeviceTrend.series;

    const buildDailyForMonth = (target) => {
      const daysInMonth = target.daysInMonth();
      const dailyCounts = new Array(daysInMonth).fill(0);
      
      deviceSalesDates.forEach((iso) => {
        const dt = dayjs(iso);
        if (!dt.isValid()) return;
        
        // Yıl ve ay karşılaştırması (dayjs month() 0-indexed: 0-11)
        if (dt.year() === target.year() && dt.month() === target.month()) {
          const dayIdx = dt.date() - 1; // date() 1-indexed: 1-31
          if (dayIdx >= 0 && dayIdx < daysInMonth) {
            dailyCounts[dayIdx] += 1;
          }
        }
      });
      
      const dailyLabels = Array.from({ length: daysInMonth }, (_, i) =>
        `${String(i + 1).padStart(2, "0")}/${target.format("MM/YYYY")}`
      );
      return { dailyLabels, dailyCounts };
    };

    if (monthlyViewMode === "thisMonth") {
      const target = dayjs().startOf("month");
      const { dailyLabels, dailyCounts } = buildDailyForMonth(target);
      labels = dailyLabels;
      data = dailyCounts;
    } else if (monthlyViewMode === "lastMonth") {
      const target = dayjs().subtract(1, "month").startOf("month");
      const { dailyLabels, dailyCounts } = buildDailyForMonth(target);
      labels = dailyLabels;
      data = dailyCounts;
    } else if (monthlyViewMode === "range" && trendStart && trendEnd) {
      // trendStart ve trendEnd "YYYY-MM" formatında (type="month" input'undan geliyor)
      const start = dayjs(trendStart + "-01"); // "YYYY-MM" -> "YYYY-MM-01"
      const end = dayjs(trendEnd + "-01").endOf("month"); // "YYYY-MM" -> ayın son günü
      
      if (start.isValid() && end.isValid() && (start.isBefore(end) || start.isSame(end, "month"))) {
        // Tarih aralığında günlük verileri göster
        const rangeLabels = [];
        const rangeData = [];
        
        // Başlangıç ve bitiş tarihleri arasındaki tüm ayları işle
        let currentMonth = start.startOf("month");
        const endMonth = end.startOf("month");
        
        // Her ay için günlük verileri topla
        while (currentMonth.isBefore(endMonth) || currentMonth.isSame(endMonth, "month")) {
          // Bu ayın günlük verilerini al
          const { dailyLabels, dailyCounts } = buildDailyForMonth(currentMonth);
          
          // Sadece seçilen tarih aralığındaki günleri ekle
          dailyLabels.forEach((label, idx) => {
            // Label formatı: "DD/MM/YYYY" (örn: "15/01/2026")
            const parts = label.split("/");
            const dayDate = dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`, "YYYY-MM-DD");
            
            if (dayDate.isValid() && dayDate.isSameOrAfter(start) && dayDate.isSameOrBefore(end)) {
              rangeLabels.push(label);
              rangeData.push(dailyCounts[idx]);
            }
          });
          
          // Sonraki aya geç
          currentMonth = currentMonth.add(1, "month");
        }
        
        if (rangeLabels.length > 0) {
          labels = rangeLabels;
          data = rangeData;
        } else {
          // Eğer günlük veri yoksa, aylık toplamları göster (fallback)
          const filteredLabels = [];
          const filteredData = [];
          monthlyDeviceTrend.labels.forEach((lbl, i) => {
            const dt = dayjs(lbl, "MM/YYYY");
            if (
              dt.isValid() &&
              dt.isSameOrAfter(start, "month") &&
              dt.isSameOrBefore(end, "month")
            ) {
              filteredLabels.push(lbl);
              filteredData.push(monthlyDeviceTrend.series[i]);
            }
          });
          if (filteredLabels.length > 0) {
            labels = filteredLabels;
            data = filteredData;
          }
        }
      }
    }

    return {
      series: [
        {
          name: "Eklenen Araç Sayısı",
          data,
        },
      ],
      options: {
        chart: {
          type: "area",
          toolbar: { show: false },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: labels,
          labels: {
            style: {
              colors: textTableColor,
            },
          },
        },
        yaxis: {
          title: {
            text: "Araç Sayısı",
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        tooltip: {
          theme: chartTheme,
          y: {
            formatter: (v) => `${v} araç`,
          },
        },
      },
    };
  }, [
    monthlyDeviceTrend,
    monthlyViewMode,
    trendStart,
    trendEnd,
    textTableColor,
    chartTheme,
    deviceSalesDates,
  ]);

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }} minH="100vh" px={{ base: 4, md: 6 }} pb={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Text fontSize="3xl" fontWeight="bold" color={headerTitleColor} mb={1}>
            Dashboard
          </Text>
          <HStack spacing={2}>
            <Icon as={FiTrendingUp} color={headerMetaIconColor} />
            <Text fontSize="sm" color={headerMetaColor}>
              Son güncelleme: {dayjs(lastRefresh).format("DD/MM/YYYY HH:mm")}
            </Text>
          </HStack>
        </Box>
        <Button
          leftIcon={<FiRefreshCw />}
          colorScheme="blue"
          variant="solid"
          onClick={fetchDashboardData}
          isLoading={loading}
          size="md"
          boxShadow="sm"
        >
          Yenile
        </Button>
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* İçerik Alanı - Loading durumunda SplashScreen göster */}
      <Box position="relative" flex={1}>
        {/* Splash Screen - Sadece içerik alanında göster */}
        {loading && stats.totalDevices === 0 && stats.totalFirms === 0 && (
          <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={100}>
            <SplashScreen isLoading={true} message="Dashboard yükleniyor..." fullScreen={false} />
          </Box>
        )}
        
        {/* Özet Kartlar */}
      <SimpleGrid minChildWidth={{ base: "240px", md: "260px" }} spacing="24px" mb="24px">
        <StatCard
          label="Toplam Araç"
          value={stats.totalDevices}
          icon={<TrackerIcon h="24px" w="24px" color={iconBoxInside} />}
          color={iconBlue}
          onClick={() => history.push("/admin/araclar")}
        />
        <StatCard
          label="Toplam Firma"
          value={stats.totalFirms}
          icon={<PersonIcon h="24px" w="24px" color={iconBoxInside} />}
          color="purple.500"
          onClick={() => history.push("/admin/hesaplar")}
        />
        <StatCard
          label="Önümüzdeki Ay Biten Abonelik"
          value={nextMonthSubscriptions.length}
          icon={<DoublePlayIcon h="24px" w="24px" color={iconBoxInside} />}
          color="orange.500"
          onClick={() => history.push("/admin/abonelik-yonetimi")}
          badge={nextMonthSubscriptions.length > 0 ? "warning" : null}
        />
      </SimpleGrid>

      {/* Cihaz tipi dağılımı + Aktif firma / araç grafiği */}
      <SimpleGrid minChildWidth={{ base: "320px", xl: "420px" }} spacing="24px" mb="24px">
        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
          transition="all 0.3s"
        >
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              Cihaz Tipine Göre Dağılım
            </Text>
            <HStack spacing={2}>
              <Icon as={FiBarChart2} color="blue.400" />
              <Text fontSize="xs" color="gray.500">
                Toplam: {stats.totalDevices}
              </Text>
            </HStack>
          </HStack>
          {deviceBrandStats.labels.length > 0 ? (
            <Chart
              options={brandChartData.options}
              series={brandChartData.series}
              type="bar"
              height={350}
            />
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Cihaz tipi verisi bulunamadı"
              height="350px"
            />
          )}
        </Box>

        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
          transition="all 0.3s"
        >
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              Aktif Firma / Araç Dağılımı
            </Text>
            <HStack spacing={2}>
              <Icon as={FiUsers} color="green.400" />
              <Text fontSize="xs" color="gray.500">
                Firmalar: {activeFirmCount} • Araçlar: {activeDeviceCount}
              </Text>
            </HStack>
          </HStack>
          {activeFirmCount + activeDeviceCount > 0 ? (
            <Chart
              options={activityChartData.options}
              series={activityChartData.series}
              type="bar"
              height={350}
            />
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Aktif firma/araç verisi bulunamadı"
              height="350px"
            />
          )}
        </Box>
      </SimpleGrid>

      {/* Aylık araç ekleme trafiği + Önümüzdeki ay bitecek abonelikler */}
      <SimpleGrid minChildWidth={{ base: "320px", xl: "420px" }} spacing="24px" mb="24px">
        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
          transition="all 0.3s"
        >
          <HStack justify="space-between" mb={2}>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              Aylık Cihaz Satış Trendi
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <HStack spacing={1}>
                <Button
                  size="xs"
                  variant={monthlyViewMode === "thisMonth" ? "solid" : "ghost"}
                  colorScheme="blue"
                  onClick={() => setMonthlyViewMode("thisMonth")}
                >
                  Bu Ay
                </Button>
                <Button
                  size="xs"
                  variant={monthlyViewMode === "lastMonth" ? "solid" : "ghost"}
                  colorScheme="blue"
                  onClick={() => setMonthlyViewMode("lastMonth")}
                >
                  Geçen Ay
                </Button>
                <Button
                  size="xs"
                  variant={monthlyViewMode === "range" ? "solid" : "ghost"}
                  colorScheme="blue"
                  onClick={() => setMonthlyViewMode("range")}
                >
                  Tarih Aralığı
                </Button>
                {monthlyViewMode === "range" && (
                  <HStack spacing={1}>
                    <Input
                      size="xs"
                      type="month"
                      value={trendStart}
                      onChange={(e) => setTrendStart(e.target.value)}
                    />
                    <Input
                      size="xs"
                      type="month"
                      value={trendEnd}
                      onChange={(e) => setTrendEnd(e.target.value)}
                    />
                  </HStack>
                )}
              </HStack>
              <HStack spacing={1}>
                <Icon as={FiActivity} color="green.400" />
                <Text fontSize="xs" color="gray.500">
                  Bu ay: {monthlyDeviceTrend.thisMonth}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Icon as={FiActivity} color="blue.400" />
                <Text fontSize="xs" color="gray.500">
                  Geçen ay: {monthlyDeviceTrend.lastMonth}
                </Text>
              </HStack>
            </HStack>
          </HStack>
          {monthlyDeviceTrend.labels.length > 0 ? (
            <Chart
              options={monthlyTrendChartData.options}
              series={monthlyTrendChartData.series}
              type="area"
              height={330}
            />
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Satış tarihi verisi bulunamadı"
              height="300px"
            />
          )}
        </Box>

        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl" }}
          transition="all 0.3s"
        >
          <Flex align="center" justify="space-between" mb={4}>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              Önümüzdeki Ay Bitecek Abonelikler
            </Text>
            <Button
              size="sm"
              colorScheme="orange"
              variant="ghost"
              onClick={() => history.push("/admin/abonelik-yonetimi")}
            >
              Abonelik Yönetimine Git →
            </Button>
          </Flex>
          {nextMonthSubscriptions.length > 0 ? (
            <VStack align="stretch" spacing={3} maxH="320px" overflowY="auto">
              {nextMonthSubscriptions.map((sub, idx) => (
                <Box
                  key={`${sub.cihaz_id}-${idx}`}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={3}
                >
                  <HStack justify="space-between">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontWeight="bold" color={textColor} fontSize="sm">
                        {sub.plaka || "-"}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {sub.firma || "Firma bilgisi yok"}
                      </Text>
                    </VStack>
                    <VStack align="flex-end" spacing={0}>
                      <Text fontSize="xs" color="gray.500">
                        Bitiş Tarihi
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="orange.400">
                        {sub.dongu_tarihi
                          ? dayjs(sub.dongu_tarihi).format("DD.MM.YYYY")
                          : "-"}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Önümüzdeki ay bitecek abonelik bulunamadı"
              height="300px"
            />
          )}
        </Box>
      </SimpleGrid>

      {/* Stok durumu & son eklenen firmalar */}
      <SimpleGrid minChildWidth={{ base: "320px", xl: "420px" }} spacing="24px">
        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl" }}
          transition="all 0.3s"
        >
          <Flex align="center" justify="space-between" mb={4}>
            <Text fontSize="lg" color={textColor} fontWeight="bold">
              Stok Durumu (Model Bazında)
            </Text>
            <Button
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => history.push("/admin/stok-yonetimi")}
            >
              Stok Yönetimine Git →
            </Button>
          </Flex>
          {stockModelSummary.length > 0 ? (
            <VStack align="stretch" spacing={2}>
              {stockModelSummary.map((item, idx) => (
                <Flex
                  key={`${item.model}-${idx}`}
                  align="center"
                  justify="space-between"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={3}
                >
                  <Text fontSize="sm" color={textColor} fontWeight="medium">
                    {item.model}
                  </Text>
                  <Text fontSize="sm" color="blue.400" fontWeight="bold">
                    {item.count} adet
                  </Text>
                </Flex>
              ))}
            </VStack>
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Stok verisi bulunamadı"
              height="220px"
            />
          )}
        </Box>

        <Box
          borderRadius="xl"
          p={6}
          bg={cardBg}
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _hover={{ boxShadow: "xl" }}
          transition="all 0.3s"
        >
          <Text fontSize="lg" color={textColor} fontWeight="bold" mb={4}>
            Son Eklenen Firmalar (Son 10)
          </Text>
          {recentFirms.length > 0 ? (
            <VStack align="stretch" spacing={2} maxH="260px" overflowY="auto">
              {recentFirms.map((firma) => (
                <Flex
                  key={firma.id}
                  align="center"
                  justify="space-between"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={3}
                >
                  <VStack align="flex-start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      {firma.firma_ismi}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {firma.sektor || "Sektör bilgisi yok"}
                    </Text>
                  </VStack>
                  <Text fontSize="xs" color="gray.500">
                    {firma.kayit_tarihi
                      ? dayjs(firma.kayit_tarihi).format("DD.MM.YYYY")
                      : "-"}
                  </Text>
                </Flex>
              ))}
            </VStack>
          ) : (
            <EmptyState
              icon={FiInbox}
              message="Firma verisi bulunamadı"
              height="220px"
            />
          )}
        </Box>
      </SimpleGrid>
      </Box>
    </Flex>
  );
}

// Stat Card Component
function StatCard({ label, value, icon, color, onClick, badge }) {
  const cardBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("gray.700", "white");
  const iconBg = useColorModeValue(color, color);
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      p={6}
      boxShadow="lg"
      borderWidth="1px"
      borderColor={borderColor}
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
      _hover={onClick ? { 
        transform: "translateY(-4px)", 
        boxShadow: "xl",
        borderColor: color,
      } : {}}
      transition="all 0.3s ease"
      position="relative"
      overflow="hidden"
    >
      {/* Decorative gradient */}
      <Box
        position="absolute"
        top={0}
        right={0}
        w="100px"
        h="100px"
        bg={`linear-gradient(135deg, ${color}15, transparent)`}
        borderRadius="full"
        transform="translate(30px, -30px)"
      />
      <Flex direction="column" position="relative" zIndex={1}>
        <Flex align="center" justify="space-between" mb={3}>
          <Stat>
            <StatLabel 
              fontSize="xs" 
              color="gray.500" 
              fontWeight="semibold" 
              textTransform="uppercase"
              letterSpacing="0.5px"
            >
              {label}
            </StatLabel>
            <StatNumber fontSize="3xl" color={textColor} fontWeight="bold" mt={1}>
              {value.toLocaleString("tr-TR")}
            </StatNumber>
          </Stat>
          <Flex 
            borderRadius="xl" 
            bg={iconBg} 
            h="56px" 
            w="56px" 
            align="center" 
            justify="center"
            boxShadow="md"
          >
            {icon}
          </Flex>
        </Flex>
        {badge && (
          <Badge colorScheme={badge} size="sm" alignSelf="flex-start" mt={2}>
            Dikkat Gerekli
          </Badge>
        )}
      </Flex>
    </Box>
  );
}

// Empty State Component
function EmptyState({ icon: IconComponent, message, height = "200px" }) {
  const textColor = useColorModeValue("gray.500", "gray.400");
  
  return (
    <Center h={height} flexDirection="column">
      <Icon as={IconComponent} boxSize={12} color={textColor} mb={3} opacity={0.5} />
      <Text color={textColor} fontSize="sm" fontWeight="medium">
        {message}
      </Text>
    </Center>
  );
}
